// FilterModalSection.tsx - collapsible search-suggestion section with direct
// navigation + "add to search" options (mirrors trade). No bottom divider;
// the SearchModal draws dividers between sections.

import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp, ArrowForward, Add } from '@mui/icons-material';

interface FilterModalSectionProps {
  title: string;
  items: string[];
  showSection: boolean;
  setShowSection: (show: boolean) => void;
  onItemClick: (value: string) => void;
  onItemNavigate?: (value: string) => void;
}

const FilterModalSection = ({
                              title,
                              items,
                              showSection,
                              setShowSection,
                              onItemClick,
                              onItemNavigate,
                            }: FilterModalSectionProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        mt: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={() => setShowSection(!showSection)}
      >
        <Box>
          <Typography
            variant='caption'
            fontWeight={'500'}
            sx={{
              color: '#000',
            }}
          >
            {title}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography
            variant='caption'
            sx={{ ml: '0.1rem', color: '#000' }}
          >
            {`(${items.length})`}
          </Typography>

          {showSection ? (
            <KeyboardArrowDown sx={{ cursor: 'pointer' }} />
          ) : (
            <KeyboardArrowUp sx={{ cursor: 'pointer' }} />
          )}
        </Box>
      </Box>

      {showSection && (
        <Box
          sx={{
            mt: 1,
            maxHeight: '8.5rem',
            overflow: 'auto',
          }}
        >
          {items
            .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
            .map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '4px 8px 4px 0',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                  gap: 1,
                }}
              >
                {/* Main item text - clickable for navigation */}
                <Typography
                  sx={{
                    cursor: 'pointer',
                    color: '#000',
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onItemNavigate) {
                      onItemNavigate(item);
                    }
                  }}
                >
                  {item}
                </Typography>

                {/* Action buttons */}
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  {/* Add to search button */}
                  <Tooltip title="Add to search" placement="top">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onItemClick(item);
                      }}
                      sx={{
                        width: 28,
                        height: 28,
                        padding: 0.5,
                        '&:hover': {
                          backgroundColor: '#e3f2fd',
                          color: '#2196f3',
                        },
                      }}
                    >
                      <Add fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {/* Navigate directly button */}
                  {onItemNavigate && (
                    <Tooltip title="View products" placement="top">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onItemNavigate(item);
                        }}
                        sx={{
                          width: 28,
                          height: 28,
                          padding: 0.5,
                          '&:hover': {
                            backgroundColor: '#e8f5e9',
                            color: '#4caf50',
                          },
                        }}
                      >
                        <ArrowForward fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            ))}
        </Box>
      )}
    </Box>
  );
};

export default FilterModalSection;
